import React, { useState, useMemo } from 'react'
import {
  FloppyDisk,
  MagnifyingGlass,
  Rocket, Target,
  Cloud, GridFour, GitBranch,
  VideoCamera, MonitorPlay, Hash,
  Calendar, CalendarCheck,  // CalendarDots — restore with the Google Calendar entry below
  // Phone, Microphone,  // Twilio / Voice & AI — hidden, see below
  ChartBar,
  Database, UsersThree, ChatCircle,
  CaretRight,
  Sparkle, Circle, GearSix, Newspaper,
  CircleNotch,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import { useSettingsStore } from '@/stores/settingsStore'
import { useAuthStore } from '@/stores/authStore'
import { usePermission } from '@/hooks/usePermission'
import { cn } from '@/lib/utils'
import { isIntegrationLockedByPlan } from '@/lib/planIntegrations'
import { inputClass, inputStyle, labelClass, labelStyle } from '@/lib/styleKit'
import { useUnsavedChangesGuard } from '@/lib/useUnsavedChangesGuard'
import type { UserConfig } from '@/types/settings'
import MaskedField from './MaskedField'
import TestConnectionButton from './TestConnectionButton'
import { ReadOnlyBanner, SectionLocked } from './SectionAccess'
import openaiLogo from '@/assets/llm-providers/openai.webp'
import anthropicLogo from '@/assets/llm-providers/anthropic.webp'
import llamaLogo from '@/assets/llm-providers/llama.webp'

// Top 3 LLM providers wired into the generation pipeline (graph.py NODE_MODELS):
// OpenAI = default engine, Anthropic = reasoning/critique fallback, Llama = self-hosted.
const AI_MODELS = [
  { name: 'OpenAI', tagline: 'Default content engine.', icon: openaiLogo },
  { name: 'Anthropic', tagline: 'Reasoning & critique layer.', icon: anthropicLogo },
  { name: 'Llama', tagline: 'Open, self-hosted models.', icon: llamaLogo },
] as const

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type IntegrationStatus = 'functional' | 'coming_soon'

type IntegrationCategory =
  | 'data_enrichment'
  | 'crm'
  | 'communication'
  | 'calendar'
  | 'voice_ai'

interface IntegrationDefinition {
  id: string
  name: string
  icon: React.ReactNode
  description: string
  category: IntegrationCategory
  status: IntegrationStatus
  keyField?: string
  setField?: keyof UserConfig
  last4Field?: keyof UserConfig
  hasClientId?: boolean
  clientIdField?: string
  clientSecretSetField?: keyof UserConfig
  secondaryField?: string
  secondaryLabel?: string
  secondarySetField?: keyof UserConfig
  secondaryLast4Field?: keyof UserConfig
  extraField?: string
  extraLabel?: string
  extraConfigField?: keyof UserConfig
  keyLabel?: string
  extraField2?: string
  extraLabel2?: string
  extraConfigField2?: keyof UserConfig
  extraField3?: string
  extraLabel3?: string
  extraConfigField3?: keyof UserConfig
  extraHint3?: string
  extraPlaceholder3?: string
  // Render the plain extra fields (e.g. Tenant ID, Client ID) BEFORE the masked
  // secret. Matches provider portal ordering (Azure lists client/tenant id then
  // secret) so users don't paste the secret into an id field by mistake.
  extrasFirst?: boolean
}

interface CategoryDefinition {
  id: IntegrationCategory
  label: string
  icon: React.ReactNode
  color: string
}

// Connection-test traffic light for the status dot:
//   none    — no key saved (empty grey ring)
//   saved   — key saved but never tested successfully (yellow)
//   success — last test passed (green)
//   failed  — last test failed (red)
type DotStatus = 'none' | 'saved' | 'success' | 'failed'

const DOT_TITLE: Record<DotStatus, string> = {
  none: 'Not connected',
  saved: 'Saved — not yet tested. Run "Test" to verify.',
  success: 'Connected — last test succeeded',
  failed: 'Last test failed — check your credentials',
}

// ---------------------------------------------------------------------------
// Category definitions
// ---------------------------------------------------------------------------

const CATEGORIES: CategoryDefinition[] = [
  { id: 'data_enrichment', label: 'Data & Enrichment', icon: <Database className="w-4 h-4" />,    color: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
  { id: 'crm',             label: 'CRM & Sales',       icon: <UsersThree className="w-4 h-4" />,  color: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
  { id: 'communication',   label: 'Communication',     icon: <ChatCircle className="w-4 h-4" />,  color: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
  { id: 'calendar',        label: 'Calendar',          icon: <Calendar className="w-4 h-4" />,    color: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
  // Twilio was the only entry, so this filter chip would render an empty list.
  // { id: 'voice_ai',        label: 'Voice & AI',        icon: <Microphone className="w-4 h-4" />,  color: 'text-[#5e6ad2] dark:text-[#a5b4fc]' },
]

// ---------------------------------------------------------------------------
// Integration catalog
// ---------------------------------------------------------------------------

const INTEGRATIONS: IntegrationDefinition[] = [
  // Data & Enrichment
  {
    id: 'apollo', name: 'Apollo.io', icon: <Rocket className="w-4 h-4" />,
    description: 'Find prospects, enrich contacts, and search companies',
    category: 'data_enrichment', status: 'functional',
    keyField: 'apollo_api_key', setField: 'apollo_api_key_set', last4Field: 'apollo_api_key_last4',
  },
  {
    id: 'hunter', name: 'Hunter.io', icon: <Target className="w-4 h-4" />,
    description: 'Find and verify professional email addresses',
    category: 'data_enrichment', status: 'functional',
    keyField: 'hunter_api_key', setField: 'hunter_api_key_set', last4Field: 'hunter_api_key_last4',
  },
  {
    id: 'peopledatalabs', name: 'People Data Labs', icon: <Database className="w-4 h-4" />,
    description: 'B2B person & company data enrichment API',
    category: 'data_enrichment', status: 'functional',
    keyField: 'peopledatalabs_api_key', setField: 'peopledatalabs_api_key_set', last4Field: 'peopledatalabs_api_key_last4',
  },
  {
    id: 'explorium', name: 'Explorium', icon: <Database className="w-4 h-4" />,
    description: 'AI-powered B2B prospect & business data enrichment',
    category: 'data_enrichment', status: 'functional',
    keyField: 'explorium_api_key', setField: 'explorium_api_key_set', last4Field: 'explorium_api_key_last4',
  },
  {
    id: 'fmp', name: 'Financial Modeling Prep', icon: <ChartBar className="w-4 h-4" />,
    description: 'Company financials: income statement, balance sheet, cash flow, valuation metrics',
    category: 'data_enrichment', status: 'functional',
    keyField: 'fmp_api_key', setField: 'fmp_api_key_set', last4Field: 'fmp_api_key_last4',
  },
  {
    id: 'alpha_vantage', name: 'Alpha Vantage', icon: <ChartBar className="w-4 h-4" />,
    description: 'Stock fundamentals, financial statements, and company overview data',
    category: 'data_enrichment', status: 'functional',
    keyField: 'alpha_vantage_api_key', setField: 'alpha_vantage_api_key_set', last4Field: 'alpha_vantage_api_key_last4',
  },
  {
    id: 'newsapi', name: 'NewsAPI.org', icon: <Newspaper className="w-4 h-4" />,
    description: 'Real-time news search across 80,000+ sources (primary news provider)',
    category: 'data_enrichment', status: 'functional',
    keyField: 'newsapi_api_key', setField: 'newsapi_api_key_set', last4Field: 'newsapi_api_key_last4',
  },

  // CRM & Sales
  {
    id: 'salesforce', name: 'Salesforce', icon: <Cloud className="w-4 h-4" />,
    description: 'CRM sync, opportunity tracking, lead management',
    category: 'crm', status: 'functional',
    keyField: 'salesforce_access_token', setField: 'salesforce_access_token_set', last4Field: 'salesforce_access_token_last4',
    keyLabel: 'Access Token',
    extraField: 'salesforce_instance_url', extraLabel: 'Instance URL (e.g. https://na1.salesforce.com)',
    extraConfigField: 'salesforce_instance_url',
  },
  {
    id: 'hubspot', name: 'HubSpot', icon: <GridFour className="w-4 h-4" />,
    description: 'CRM sync, deal pipeline, marketing automation',
    category: 'crm', status: 'functional',
    keyField: 'hubspot_api_key', setField: 'hubspot_api_key_set', last4Field: 'hubspot_api_key_last4',
    keyLabel: 'Private App Token',
  },
  {
    id: 'pipedrive', name: 'Pipedrive', icon: <GitBranch className="w-4 h-4" />,
    description: 'Deal management and sales pipeline tracking',
    category: 'crm', status: 'functional',
    keyField: 'pipedrive_api_key', setField: 'pipedrive_api_key_set', last4Field: 'pipedrive_api_key_last4',
    keyLabel: 'API Token',
  },
  {
    id: 'zoho', name: 'Zoho CRM', icon: <UsersThree className="w-4 h-4" />,
    description: 'Import Zoho contacts & leads, CRM sync, deal creation',
    category: 'crm', status: 'functional',
    keyField: 'zoho_refresh_token', setField: 'zoho_refresh_token_set', last4Field: 'zoho_refresh_token_last4',
    keyLabel: 'Refresh Token',
    secondaryField: 'zoho_client_secret', secondaryLabel: 'Client Secret',
    secondarySetField: 'zoho_client_secret_set', secondaryLast4Field: 'zoho_client_secret_last4',
    extraField: 'zoho_client_id', extraLabel: 'Client ID',
    extraConfigField: 'zoho_client_id',
    extraField2: 'zoho_region', extraLabel2: 'Data Center (com, eu, in, com.au, jp — default com)',
    extraConfigField2: 'zoho_region',
  },

  // Communication
  {
    id: 'zoom', name: 'Zoom', icon: <VideoCamera className="w-4 h-4" />,
    description: 'Create meetings via Server-to-Server OAuth',
    category: 'communication', status: 'functional',
    keyField: 'zoom_client_secret', setField: 'zoom_client_secret_set', last4Field: 'zoom_client_secret_last4',
    keyLabel: 'Client Secret',
    extraField: 'zoom_account_id', extraLabel: 'Account ID',
    extraConfigField: 'zoom_account_id',
    extraField2: 'zoom_client_id', extraLabel2: 'Client ID',
    extraConfigField2: 'zoom_client_id',
    extrasFirst: true,
  },
  {
    id: 'teams', name: 'Microsoft Teams', icon: <MonitorPlay className="w-4 h-4" />,
    description: 'Notifications and team updates via webhook',
    category: 'communication', status: 'functional',
    keyField: 'teams_webhook_url', setField: 'teams_webhook_url_set', last4Field: 'teams_webhook_url_last4',
    keyLabel: 'Webhook URL',
  },
  {
    id: 'slack', name: 'Slack', icon: <Hash className="w-4 h-4" />,
    description: 'Notifications, alerts, and team updates',
    category: 'communication', status: 'functional',
    keyField: 'slack_webhook_url', setField: 'slack_webhook_url_set', last4Field: 'slack_webhook_url_last4',
    keyLabel: 'Webhook URL',
  },
  // Google Meet — hidden from the catalog (product decision). Restore this
  // entry (and the CalendarDots/VideoCamera imports) to bring it back.
  // {
  //   id: 'google_meet', name: 'Google Meet', icon: <VideoCamera className="w-4 h-4" />,
  //   description: 'Meeting scheduling and video calls',
  //   category: 'communication', status: 'coming_soon',
  // },

  // Calendar
  // Google Calendar — hidden from the catalog (product decision). The backend
  // integration and its google_service_account_json field are untouched.
  // {
  //   id: 'google_calendar', name: 'Google Calendar', icon: <CalendarDots className="w-4 h-4" />,
  //   description: 'Meeting scheduling via Google Service Account',
  //   category: 'calendar', status: 'functional',
  //   keyField: 'google_service_account_json', setField: 'google_service_account_json_set',
  //   keyLabel: 'Service Account JSON',
  // },
  {
    id: 'outlook_calendar', name: 'Outlook Calendar', icon: <CalendarCheck className="w-4 h-4" />,
    description: 'Book meetings and read availability via Microsoft Graph (app registration)',
    category: 'calendar', status: 'functional',
    keyField: 'microsoft_client_secret', setField: 'microsoft_client_secret_set', last4Field: 'microsoft_client_secret_last4',
    keyLabel: 'Client Secret (value)',
    extraField: 'microsoft_tenant_id', extraLabel: 'Directory (tenant) ID',
    extraConfigField: 'microsoft_tenant_id',
    extraField2: 'microsoft_client_id', extraLabel2: 'Application (client) ID',
    extraConfigField2: 'microsoft_client_id',
    extraField3: 'microsoft_host_email', extraLabel3: 'Meeting host mailbox',
    extraConfigField3: 'microsoft_host_email',
    extraPlaceholder3: 'name@yourdomain.com',
    extraHint3: 'The mailbox meetings are booked on and invites are sent from. Must be a licensed mailbox in your Microsoft 365 tenant. Click “Test Outlook” to confirm it exists.',
    extrasFirst: true,
  },
  {
    id: 'calendly', name: 'Calendly', icon: <CalendarCheck className="w-4 h-4" />,
    description: 'Meeting booking links and scheduling pages',
    category: 'calendar', status: 'functional',
    keyField: 'calendly_api_key', setField: 'calendly_api_key_set', last4Field: 'calendly_api_key_last4',
    keyLabel: 'Personal Access Token',
  },

  // Voice & AI — hidden from Settings. Uncomment this entry and the 'voice_ai'
  // CATEGORIES row (plus the Phone/Microphone icon imports) to bring it back.
  // The backend fields and credentials are untouched.
  // {
  //   id: 'twilio', name: 'Twilio', icon: <Phone className="w-4 h-4" />,
  //   description: 'SMS follow-ups and phone verification',
  //   category: 'voice_ai', status: 'functional',
  //   keyField: 'twilio_account_sid', setField: 'twilio_auth_token_set', last4Field: 'twilio_auth_token_last4',
  //   keyLabel: 'Account SID',
  //   secondaryField: 'twilio_auth_token', secondaryLabel: 'Auth Token',
  //   secondarySetField: 'twilio_auth_token_set', secondaryLast4Field: 'twilio_auth_token_last4',
  //   extraField: 'twilio_phone_number', extraLabel: 'Phone Number (e.g. +1234567890)',
  //   extraConfigField: 'twilio_phone_number',
  // },

]

/**
 * `data-settings-anchor` slug for one integration row, so the settings search
 * can deep-link to it. Catalog ids are the source of truth, but a few carry
 * underscores (`alpha_vantage`, `outlook_calendar`) and `SettingsPage` only
 * resolves lowercase-kebab slugs — so normalise here, and write the normalised
 * form into `SettingsNav`'s keyword table.
 */
const anchorSlug = (id: string) => `integrations-${id.replace(/_/g, '-')}`

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// Plain (non-secret) extra field — e.g. Twilio phone number, Salesforce
// instance URL. Mirrors MaskedField's inline Save button so these fields have
// the same save affordance as the masked Account SID / Auth Token, instead of
// only committing silently on blur (which read as "no save option").
const ExtraFieldInput: React.FC<{
  label: string
  field: string
  configField?: keyof UserConfig
  config: UserConfig | null
  pendingKeys: Record<string, string>
  setPendingKeys: React.Dispatch<React.SetStateAction<Record<string, string>>>
  commitExtra: (field: string, configField?: keyof UserConfig) => Promise<void>
  hint?: string
  placeholder?: string
  /** View-only: input and Save are disabled. See MaskedField's `readOnly`. */
  readOnly?: boolean
}> = ({ label, field, configField, config, pendingKeys, setPendingKeys, commitExtra, hint, placeholder, readOnly = false }) => {
  const [saving, setSaving] = useState(false)
  const saved = configField ? ((config?.[configField] as string) || '') : ''
  const value = pendingKeys[field] ?? saved
  const isDirty = pendingKeys[field] !== undefined && pendingKeys[field] !== saved

  const handleSave = async () => {
    if (!isDirty || saving) return
    setSaving(true)
    try {
      await commitExtra(field, configField)
    } finally {
      setSaving(false)
    }
  }

  // `field` is the config column name (e.g. `salesforce_instance_url`) — unique
  // per rendered field, so it makes a stable id for the label association.
  const fieldId = `integration-extra-${field}`

  return (
    <div>
      <label htmlFor={fieldId} className={labelClass} style={labelStyle}>{label}</label>
      <div className="flex items-center gap-2">
        <input
          id={fieldId}
          type="text"
          value={value}
          onChange={e => setPendingKeys(prev => ({ ...prev, [field]: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave() }}
          placeholder={placeholder || label}
          className={cn(inputClass, 'flex-1')}
          style={inputStyle}
          disabled={saving || readOnly}
        />
        <Tooltip label="Save">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving || readOnly}
            className="h-9 w-9 flex items-center justify-center rounded-md bg-[#5e6ad2] text-white hover:bg-[#7170ff] active:bg-[#5058c0] transition-colors duration-150 disabled:opacity-50"
            aria-label="Save"
          >
            {saving
              ? <CircleNotch className="w-4 h-4 animate-spin" weight="bold" />
              : <FloppyDisk className="w-4 h-4" weight="bold" />}
          </button>
        </Tooltip>
      </div>
      {hint && (
        <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground dark:text-[#8a8f98]">
          {hint}
        </p>
      )}
    </div>
  )
}

/**
 * Whether this provider is outside the account's plan, and so must not be
 * connectable (AB#22375).
 *
 * `allowed_integrations` comes from GET /auth/plan-status — the same plan row
 * the Billing cards render from, sent as data so this file never mirrors the
 * per-tier lists. Two deliberate no-ops, both matching the server's gate:
 *
 *  - a null list means the plan carries no list at all, which the backend
 *    treats as "no restriction" rather than "nothing allowed"; guessing the
 *    other way would lock every integration on a seeding gap.
 *  - an ALREADY-CONNECTED provider is never locked. The gate grandfathers
 *    those (the bug shipped, so accounts hold out-of-plan credentials and a
 *    key that works today must stay rotatable), and showing it as locked
 *    would misdescribe a live connection.
 *
 * UX only — PUT /auth/config re-checks all of this.
 */
function usePlanLockedIntegration(int: IntegrationDefinition): boolean {
  const allowed = useAuthStore((s) => s.planStatus?.allowed_integrations)
  const { config } = useSettingsStore()
  const alreadyConnected = int.setField ? Boolean(config?.[int.setField]) : false
  return isIntegrationLockedByPlan(allowed, int.id, alreadyConnected)
}

const StandardConfigPanel: React.FC<{
  integration: IntegrationDefinition
  config: UserConfig | null
  pendingKeys: Record<string, string>
  setPendingKeys: React.Dispatch<React.SetStateAction<Record<string, string>>>
  testIntegration: (service: string, apiKey: string, displayName?: string) => Promise<any>
}> = ({ integration: int, config, pendingKeys, setPendingKeys, testIntegration }) => {
  const { updateConfig, revealConfigKey } = useSettingsStore()
  // Manager's "view" level on integrations.manage sees this panel but can't
  // mutate it -- Owner/Admin keep full write access ("manage").
  const { canWrite: roleCanWrite } = usePermission('integrations', 'manage')
  // Two independent axes: the ROLE may configure integrations, and the PLAN
  // has to include this particular provider (AB#22375 -- the plan's advertised
  // list was read by nothing, so any tier could connect anything). Folded into
  // the same `canWrite` the fields already key off, so every input and the
  // save button go read-only without touching each one.
  const lockedByPlan = usePlanLockedIntegration(int)
  const canWrite = roleCanWrite && !lockedByPlan
  const isSet = int.setField ? (config?.[int.setField] as boolean || false) : false
  const last4 = int.last4Field ? (config?.[int.last4Field] as string || undefined) : undefined

  // Plain (non-secret) extra fields like Instance URL / Tenant ID persist only on
  // explicit save (button or Enter). Test Connection commits any pending extras
  // first, so a filled-but-unsaved field (e.g. Salesforce Instance URL) can't
  // break the connection test.
  const commitExtra = async (field: string, configField?: keyof UserConfig) => {
    const pending = pendingKeys[field]
    if (pending === undefined) return
    const saved = configField ? ((config?.[configField] as string) || '') : ''
    if (pending === saved) return
    await updateConfig({ [field]: pending })
    setPendingKeys(prev => {
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const maskedFields = (
    <>
      <MaskedField
        label={int.keyLabel || 'API Key'}
        isSet={int.id === 'twilio' ? Boolean(config?.twilio_account_sid) : isSet}
        last4={int.id === 'twilio' ? (config?.twilio_account_sid ? config.twilio_account_sid.slice(-4) : undefined) : last4}
        placeholder={`Enter ${int.name} ${int.keyLabel || 'API key'}`}
        onSave={(val) => setPendingKeys(prev => ({ ...prev, [int.keyField!]: val }))}
        onClear={() => setPendingKeys(prev => ({ ...prev, [int.keyField!]: '' }))}
        clearTooltip="Remove key — applies when you save"
        clearDescription={`Nothing is deleted yet. The stored ${int.name} ${int.keyLabel || 'API key'} is removed from your account when you press "Save All Changes" at the bottom of this page — leave the page without saving and it stays. You can add it again at any time.`}
        clearConfirmLabel="Remove on save"
        onReveal={() => revealConfigKey(int.keyField!)}
        onCommit={async (val) => { await updateConfig({ [int.keyField!]: val }) }}
        readOnly={!canWrite}
      />
      {int.secondaryField && (
        <MaskedField
          label={int.secondaryLabel || 'Secret'}
          isSet={int.secondarySetField ? (config?.[int.secondarySetField] as boolean || false) : false}
          last4={int.secondaryLast4Field ? (config?.[int.secondaryLast4Field] as string || undefined) : undefined}
          placeholder={`Enter ${int.secondaryLabel || 'secret'}`}
          onSave={(val) => setPendingKeys(prev => ({ ...prev, [int.secondaryField!]: val }))}
          onClear={() => setPendingKeys(prev => ({ ...prev, [int.secondaryField!]: '' }))}
          clearTooltip="Remove secret — applies when you save"
          clearDescription={`Nothing is deleted yet. The stored ${int.name} ${int.secondaryLabel || 'secret'} is removed from your account when you press "Save All Changes" at the bottom of this page — leave the page without saving and it stays. You can add it again at any time.`}
          clearConfirmLabel="Remove on save"
          onReveal={() => revealConfigKey(int.secondaryField!)}
          onCommit={async (val) => { await updateConfig({ [int.secondaryField!]: val }) }}
          readOnly={!canWrite}
        />
      )}
    </>
  )

  const extraFields = (
    <>
      {int.extraField && (
        <ExtraFieldInput
          label={int.extraLabel || int.extraField}
          field={int.extraField}
          configField={int.extraConfigField}
          config={config}
          pendingKeys={pendingKeys}
          setPendingKeys={setPendingKeys}
          commitExtra={commitExtra}
          readOnly={!canWrite}
        />
      )}
      {int.extraField2 && (
        <ExtraFieldInput
          label={int.extraLabel2 || int.extraField2}
          field={int.extraField2}
          configField={int.extraConfigField2}
          config={config}
          pendingKeys={pendingKeys}
          setPendingKeys={setPendingKeys}
          commitExtra={commitExtra}
          readOnly={!canWrite}
        />
      )}
      {int.extraField3 && (
        <ExtraFieldInput
          label={int.extraLabel3 || int.extraField3}
          field={int.extraField3}
          configField={int.extraConfigField3}
          config={config}
          pendingKeys={pendingKeys}
          setPendingKeys={setPendingKeys}
          commitExtra={commitExtra}
          hint={int.extraHint3}
          placeholder={int.extraPlaceholder3}
          readOnly={!canWrite}
        />
      )}
    </>
  )

  return (
    <>
      {int.extrasFirst ? <>{extraFields}{maskedFields}</> : <>{maskedFields}{extraFields}</>}
      {/* Testing a connection probes the stored key and rewrites its saved
          verdict, so it is a write: hidden for "view" (the route 403s it too). */}
      {lockedByPlan && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-md border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2 mb-3 dark:border-amber-500/25 dark:bg-amber-500/[0.08]">
          <p
            className="text-amber-700 dark:text-amber-400"
            style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
          >
            {int.name} isn&apos;t included in your plan, so it can&apos;t be connected.
          </p>
          <a
            href="/billing"
            className="text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline whitespace-nowrap"
            style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
          >
            View plans
          </a>
        </div>
      )}

      {canWrite && (
        <div className="flex items-center justify-between">
          <TestConnectionButton
            label={`Test ${int.name}`}
            onTest={async () => {
              if (int.extraField) await commitExtra(int.extraField, int.extraConfigField)
              if (int.extraField2) await commitExtra(int.extraField2, int.extraConfigField2)
              if (int.extraField3) await commitExtra(int.extraField3, int.extraConfigField3)
              return testIntegration(int.id, pendingKeys[int.keyField!] || 'existing', int.name)
            }}
          />
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// LinkedIn OAuth config panel - DISABLED / SHELVED (2026-08-28)
//
// The LinkedIn provider was removed from the Integrations list, so nothing
// renders this any more. Kept commented out rather than deleted so the provider
// can be brought back without rebuilding the OAuth UI. Restoring it needs, in
// this file: the `linkedin` entry in INTEGRATIONS, the `LinkedinLogo` import,
// the linkedInId / pendingLinkedInSecret state + config effect in the parent,
// the linkedin branches in isConnected/getDotStatus/hasPendingChanges/handleSave,
// and the linkedInId / setLinkedInId / setPendingLinkedInSecret / onRefreshConfig
// props threaded through IntegrationRow. Backend side: see the restore checklist
// in backend/app/services/linkedin_service.py.
// ---------------------------------------------------------------------------

// const LinkedInConfigPanel: React.FC<{
//   config: UserConfig | null
//   linkedInId: string
//   setLinkedInId: (val: string) => void
//   setPendingLinkedInSecret: (val: string | null) => void
//   onRefreshConfig: () => void
// }> = ({ config, linkedInId, setLinkedInId, setPendingLinkedInSecret, onRefreshConfig }) => {
//   const [oauthLoading, setOauthLoading] = React.useState(false)
//   const [disconnecting, setDisconnecting] = React.useState(false)
//   const [savingCreds, setSavingCreds] = React.useState(false)
//   const [localSecret, setLocalSecret] = React.useState<string | null>(null)
//   const { updateConfig, revealConfigKey } = useSettingsStore()
//   const { canWrite } = usePermission('integrations', 'manage')
//
//   const isOAuthConnected = config?.linkedin_oauth_connected || false
//   const hasCredentials = config?.linkedin_client_secret_set && Boolean(config?.linkedin_client_id)
//
//   const hasLocalChanges =
//     localSecret !== null ||
//     linkedInId !== (config?.linkedin_client_id || '')
//
//   const handleSaveCredentials = async () => {
//     const data: Record<string, any> = {}
//     if (linkedInId !== (config?.linkedin_client_id || '')) {
//       data.linkedin_client_id = linkedInId
//     }
//     if (localSecret !== null) {
//       data.linkedin_client_secret = localSecret
//       setPendingLinkedInSecret(null)
//     }
//     if (Object.keys(data).length === 0) return
//     setSavingCreds(true)
//     try {
//       await updateConfig(data)
//       setLocalSecret(null)
//     } finally {
//       setSavingCreds(false)
//     }
//   }
//
//   const handleConnect = async () => {
//     setOauthLoading(true)
//     try {
//       const { integrationsApi } = await import('@/lib/api')
//       const redirectUri = `${window.location.origin}/settings/linkedin/callback`
//       const resp = await integrationsApi.linkedinAuthorize(redirectUri)
//       const { authorize_url, state } = resp.data
//       sessionStorage.setItem('linkedin_oauth_state', state)
//       sessionStorage.setItem('linkedin_oauth_redirect', redirectUri)
//       window.location.href = authorize_url
//     } catch (err: any) {
//       const msg = err?.response?.data?.detail || 'Failed to start OAuth flow'
//       const { toast } = await import('sonner')
//       toast.error(msg)
//     } finally {
//       setOauthLoading(false)
//     }
//   }
//
//   const handleDisconnect = async () => {
//     setDisconnecting(true)
//     try {
//       const { integrationsApi } = await import('@/lib/api')
//       await integrationsApi.linkedinDisconnect()
//       const { toast } = await import('sonner')
//       toast.success('LinkedIn disconnected')
//       onRefreshConfig()
//     } catch {
//       const { toast } = await import('sonner')
//       toast.error('Failed to disconnect')
//     } finally {
//       setDisconnecting(false)
//     }
//   }
//
//   return (
//     <>
//       {/* OAuth Status */}
//       <div className={cn(
//         'flex items-center gap-3 px-3.5 py-3 rounded-md border',
//         isOAuthConnected
//           ? 'border-emerald-500/25 bg-emerald-500/[0.08] dark:border-emerald-500/30 dark:bg-emerald-500/[0.10]'
//           : 'border-border/50 bg-muted/40 dark:border-white/[0.08] dark:bg-white/[0.02]',
//       )}>
//         <div className={cn(
//           'w-2.5 h-2.5 rounded-full flex-shrink-0',
//           isOAuthConnected ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-muted-foreground/30 dark:bg-white/[0.10]',
//         )} />
//         <div className="flex-1 min-w-0">
//           <p className={cn(
//             isOAuthConnected ? 'text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground dark:text-[#8a8f98]',
//           )} style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
//             {isOAuthConnected ? 'OAuth Connected' : 'Not Connected'}
//           </p>
//           {isOAuthConnected && config?.linkedin_token_expires_at && (
//             <p className="text-emerald-600 dark:text-emerald-400 tabular-nums" style={{ fontSize: '10px' }}>
//               Expires: {new Date(config.linkedin_token_expires_at).toLocaleDateString()}
//             </p>
//           )}
//         </div>
//         {isOAuthConnected ? (
//           canWrite && (
//             <button
//               type="button"
//               onClick={handleDisconnect}
//               disabled={disconnecting}
//               className="h-8 px-3 text-red-600 dark:text-red-400 bg-red-500/[0.08] dark:bg-red-500/[0.10] border border-red-500/25 dark:border-red-500/30 rounded-md hover:bg-red-500/[0.12] dark:hover:bg-red-500/[0.14] transition-colors duration-150 disabled:opacity-50"
//               style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
//             >
//               {disconnecting ? 'Disconnecting...' : 'Disconnect'}
//             </button>
//           )
//         ) : hasCredentials && canWrite ? (
//           <button
//             type="button"
//             onClick={handleConnect}
//             disabled={oauthLoading}
//             className="h-8 px-3 text-[#5e6ad2] dark:text-[#a5b4fc] bg-[#5e6ad2]/[0.08] dark:bg-[#5e6ad2]/[0.12] border border-[#5e6ad2]/25 dark:border-[#5e6ad2]/30 rounded-md hover:bg-[#5e6ad2]/[0.14] dark:hover:bg-[#5e6ad2]/[0.18] transition-colors duration-150 disabled:opacity-50"
//             style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
//           >
//             {oauthLoading ? 'Redirecting...' : 'Connect with LinkedIn'}
//           </button>
//         ) : !hasCredentials ? (
//           <span className="text-amber-700 dark:text-amber-300 uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>Set credentials first</span>
//         ) : null}
//       </div>
//
//       {/* Client Credentials */}
//       <div>
//         <label htmlFor="linkedin-client-id" className={labelClass} style={labelStyle}>Client ID</label>
//         <input
//           id="linkedin-client-id"
//           type="text"
//           value={linkedInId}
//           onChange={e => setLinkedInId(e.target.value)}
//           placeholder="LinkedIn Client ID"
//           className={inputClass}
//           style={inputStyle}
//           disabled={!canWrite}
//         />
//       </div>
//       <MaskedField
//         label="Client Secret"
//         isSet={config?.linkedin_client_secret_set || false}
//         placeholder="Enter LinkedIn Client Secret"
//         onSave={(val) => { setLocalSecret(val); setPendingLinkedInSecret(val) }}
//         onClear={() => { setLocalSecret(''); setPendingLinkedInSecret('') }}
//         clearTooltip="Remove secret — applies when you save"
//         clearDescription={'Nothing is deleted yet. The stored LinkedIn Client Secret is removed from your account when you press "Save Credentials" below — leave the page without saving and it stays. You can add it again at any time.'}
//         clearConfirmLabel="Remove on save"
//         onReveal={() => revealConfigKey('linkedin_client_secret')}
//         readOnly={!canWrite}
//       />
//       {hasLocalChanges && canWrite && (
//         <button
//           type="button"
//           onClick={handleSaveCredentials}
//           disabled={savingCreds}
//           className="w-full h-9 px-4 bg-[#5e6ad2] text-white rounded-md hover:bg-[#7170ff] active:bg-[#5058c0] transition-colors duration-150 disabled:opacity-50"
//           style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
//         >
//           {savingCreds ? 'Saving...' : 'Save Credentials'}
//         </button>
//       )}
//       <p className="text-muted-foreground dark:text-[#62666d]" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>
//         Save your Client ID and Secret, then click "Connect with LinkedIn" to authorize.
//       </p>
//     </>
//   )
// }

// ---------------------------------------------------------------------------
// Integration Row
// ---------------------------------------------------------------------------

const IntegrationRow: React.FC<{
  integration: IntegrationDefinition
  isConnected: boolean
  dotStatus: DotStatus
  isExpanded: boolean
  onToggle: () => void
  config: UserConfig | null
  pendingKeys: Record<string, string>
  setPendingKeys: React.Dispatch<React.SetStateAction<Record<string, string>>>
  testIntegration: (service: string, apiKey: string, displayName?: string) => Promise<any>
}> = ({
  integration: int,
  isConnected,
  dotStatus,
  isExpanded,
  onToggle,
  config,
  pendingKeys,
  setPendingKeys,
  testIntegration,
}) => {
  const isComingSoon = int.status === 'coming_soon'

  return (
    <div
      // Deep-link target for the settings search (SettingsNav mints the same
      // slug). `anchorSlug` normalises the catalog id, which is the only place
      // the two sides can drift.
      data-settings-anchor={anchorSlug(int.id)}
      className={cn(
        'group transition-colors duration-200',
        isExpanded && 'bg-muted/40 dark:bg-white/[0.02] rounded-md -mx-2 px-2',
      )}
    >
      {/* Row */}
      <button
        type="button"
        disabled={isComingSoon}
        onClick={onToggle}
        className={cn(
          'w-full flex items-center gap-3 py-3 px-2 rounded-md text-left transition-colors duration-150',
          isComingSoon
            ? 'opacity-60 cursor-not-allowed'
            : 'hover:bg-muted/50 dark:hover:bg-white/[0.04] cursor-pointer',
          isExpanded && 'hover:bg-transparent dark:hover:bg-transparent',
        )}
      >
        {/* Status dot — saved=yellow, tested-ok=green, tested-failed=red */}
        <div className="flex-shrink-0 w-5 flex justify-center" title={DOT_TITLE[isComingSoon ? 'none' : dotStatus]}>
          {isComingSoon ? (
            <Sparkle className="w-3.5 h-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
          ) : dotStatus === 'success' ? (
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 dark:bg-emerald-400 ring-[3px] ring-emerald-500/20 dark:ring-emerald-400/20" />
          ) : dotStatus === 'failed' ? (
            <div className="w-2.5 h-2.5 rounded-full bg-red-500 dark:bg-red-400 ring-[3px] ring-red-500/20 dark:ring-red-400/20" />
          ) : dotStatus === 'saved' ? (
            <div className="w-2.5 h-2.5 rounded-full bg-amber-500 dark:bg-amber-400 ring-[3px] ring-amber-500/20 dark:ring-amber-400/20" />
          ) : (
            <Circle className="w-2.5 h-2.5 text-muted-foreground/40 dark:text-[#3e3e44]" />
          )}
        </div>

        {/* Icon */}
        <div className={cn(
          'w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 transition-colors duration-150 border',
          isComingSoon
            ? 'bg-muted/50 border-border/40 text-muted-foreground/50 dark:bg-white/[0.03] dark:border-white/[0.05] dark:text-[#62666d]'
            : dotStatus === 'success'
              ? 'bg-emerald-500/[0.08] border-emerald-500/20 text-emerald-700 dark:bg-emerald-500/[0.12] dark:border-emerald-500/25 dark:text-emerald-300'
              : dotStatus === 'failed'
                ? 'bg-red-500/[0.08] border-red-500/20 text-red-700 dark:bg-red-500/[0.12] dark:border-red-500/25 dark:text-red-300'
                : dotStatus === 'saved'
                  ? 'bg-amber-500/[0.08] border-amber-500/20 text-amber-700 dark:bg-amber-500/[0.12] dark:border-amber-500/25 dark:text-amber-300'
                  : 'bg-muted/50 border-border/40 text-muted-foreground dark:bg-white/[0.04] dark:border-white/[0.05] dark:text-[#8a8f98] group-hover:bg-[#5e6ad2]/[0.08] group-hover:border-[#5e6ad2]/20 group-hover:text-[#5e6ad2] dark:group-hover:bg-[#5e6ad2]/[0.12] dark:group-hover:border-[#5e6ad2]/25 dark:group-hover:text-[#a5b4fc]',
        )}>
          {int.icon}
        </div>

        {/* Name + description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}>
              {int.name}
            </span>
            {isComingSoon && (
              <span className="text-[#5e6ad2] dark:text-[#a5b4fc] bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.14] border border-[#5e6ad2]/25 dark:border-[#5e6ad2]/30 px-2 py-0.5 rounded-full uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>
                Soon
              </span>
            )}
          </div>
          <p className="text-muted-foreground dark:text-[#8a8f98] truncate" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
            {int.description}
          </p>
        </div>

        {/* Right side: key hint + chevron */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {isConnected && !isExpanded && int.last4Field && (
            <span className="hidden sm:block text-muted-foreground dark:text-[#62666d] font-mono bg-muted/50 dark:bg-white/[0.04] border border-border/50 dark:border-white/[0.06] px-2.5 py-0.5 rounded-full" style={{ fontSize: '10px' }}>
              ****{(config?.[int.last4Field] as string) || ''}
            </span>
          )}
          {!isComingSoon && (
            <CaretRight className={cn(
              'w-4 h-4 text-muted-foreground/40 dark:text-[#3e3e44] transition-transform duration-200',
              isExpanded && 'rotate-90 text-muted-foreground/60 dark:text-[#62666d]',
            )} weight="bold" />
          )}
        </div>
      </button>

      {/* Expanded config panel */}
      {isExpanded && !isComingSoon && (
        <div
          className="pb-3 pt-1 px-2 ml-[52px]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-3 p-5 bg-card dark:bg-[#0f1011] border border-border/50 dark:border-white/[0.08] rounded-md">
            <StandardConfigPanel
              integration={int}
              config={config}
              pendingKeys={pendingKeys}
              setPendingKeys={setPendingKeys}
              testIntegration={testIntegration}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const IntegrationsSection: React.FC = () => {
  const { config, updateConfig, testIntegration, isSaving } = useSettingsStore()

  // §4-C: Owner/Admin manage, Manager view-only, Rep/Analyst no access. The
  // nav entry is already hidden for the last two, so this only catches a
  // direct/deep link -- and the config payload they receive is stripped
  // backend-side either way, so there is nothing here for them to render.
  const { allowed: integrationsAllowed, canWrite } = usePermission('integrations', 'manage')

  const [searchQuery, setSearchQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<IntegrationCategory | 'all'>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const [pendingKeys, setPendingKeys] = useState<Record<string, string>>({})
  useUnsavedChangesGuard(Object.keys(pendingKeys).length > 0)
  const isConnected = (int: IntegrationDefinition): boolean => {
    if (int.id === 'twilio') return (config?.twilio_auth_token_set && Boolean(config?.twilio_account_sid)) || false
    if (int.id === 'zoom') return (config?.zoom_client_secret_set && Boolean(config?.zoom_account_id) && Boolean(config?.zoom_client_id)) || false
    if (int.id === 'salesforce') return (config?.salesforce_access_token_set && Boolean(config?.salesforce_instance_url)) || false
    if (int.id === 'zoho') return (config?.zoho_refresh_token_set && config?.zoho_client_secret_set && Boolean(config?.zoho_client_id)) || false
    if (int.id === 'google_calendar') return config?.google_service_account_json_set || false
    if (int.id === 'outlook_calendar') return (config?.microsoft_client_secret_set && Boolean(config?.microsoft_tenant_id)) || false
    if (int.setField) return (config?.[int.setField] as boolean) || false
    return false
  }

  // Status dot color: yellow once a key is saved, green/red after a successful/
  // failed connection test.
  const getDotStatus = (int: IntegrationDefinition): DotStatus => {
    if (int.status !== 'functional' || !isConnected(int)) return 'none'
    const test = config?.integration_test_status?.[int.id]
    if (test?.status === 'success') return 'success'
    if (test?.status === 'failed') return 'failed'
    return 'saved'
  }

  const connectedCount = INTEGRATIONS.filter(
    i => i.status === 'functional' && isConnected(i)
  ).length
  const totalFunctional = INTEGRATIONS.filter(i => i.status === 'functional').length

  const filteredIntegrations = useMemo(() => {
    let result = INTEGRATIONS

    if (activeCategory !== 'all') {
      result = result.filter(i => i.category === activeCategory)
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(i =>
        i.name.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q)
      )
    }

    return result
  }, [searchQuery, activeCategory])

  // Group filtered integrations by category (preserving order)
  const groupedIntegrations = useMemo(() => {
    const groups: { category: CategoryDefinition; items: IntegrationDefinition[] }[] = []
    const catMap = new Map<IntegrationCategory, IntegrationDefinition[]>()

    for (const int of filteredIntegrations) {
      if (!catMap.has(int.category)) catMap.set(int.category, [])
      catMap.get(int.category)!.push(int)
    }

    // Preserve the fixed catalog order within each category — do NOT reorder by
    // connection state, otherwise rows shuffle every time a key is saved.
    for (const cat of CATEGORIES) {
      const items = catMap.get(cat.id)
      if (!items || items.length === 0) continue
      groups.push({ category: cat, items })
    }

    return groups
  }, [filteredIntegrations])

  const hasPendingChanges = Object.keys(pendingKeys).length > 0

  if (!integrationsAllowed) {
    return (
      <SectionLocked title="Integrations are managed by your account owner">
        Your role doesn&apos;t include access to third-party connections or their
        API keys. Ask a workspace Owner or Admin to connect a service for you.
      </SectionLocked>
    )
  }

  const handleSave = async () => {
    const data: Record<string, any> = { ...pendingKeys }
    if (Object.keys(data).length === 0) return
    await updateConfig(data)
    setPendingKeys({})
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2
            className="text-foreground dark:text-[#f7f8f8]"
            style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
          >
            Integrations
          </h2>
          <p
            className="text-muted-foreground dark:text-[#8a8f98] mt-1"
            style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
          >
            Connect third-party services for enrichment, CRM, analytics, and more.
          </p>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 dark:bg-emerald-500/[0.12] border border-emerald-500/25 dark:border-emerald-500/30">
          <div className="w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400" />
          <span className="text-emerald-700 dark:text-emerald-300 tabular-nums uppercase tracking-[0.06em]" style={{ fontSize: '11px', fontWeight: 590 }}>
            {connectedCount}/{totalFunctional}
          </span>
        </div>
      </div>

      {!canWrite && (
        <ReadOnlyBanner>
          You can see which services are connected, but only a workspace Owner or
          Admin can add, change, test, or remove integration credentials. Stored
          keys stay hidden — they are never sent to your browser.
        </ReadOnlyBanner>
      )}

      {/* Cutting-edge AI models banner */}
      <div
        className="relative overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/[0.04] dark:shadow-none dark:ring-0"
        style={{ background: 'linear-gradient(105deg, #14152b 0%, #21245f 36%, #3a48a4 68%, #5160c6 100%)' }}
      >
        {/* Atmosphere — radial glows + dot grid (design-aesthetics §8) */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div
            className="absolute -top-24 right-12 h-64 w-64 rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(124,141,240,0.45), transparent 70%)' }}
          />
          <div
            className="absolute -bottom-20 left-1/4 h-56 w-56 rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(113,112,255,0.30), transparent 70%)' }}
          />
          <div
            className="absolute inset-0 opacity-[0.16]"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.35) 1px, transparent 1px)',
              backgroundSize: '26px 26px',
            }}
          />
        </div>

        {/* Content */}
        <div className="relative flex items-center gap-6 p-6 sm:p-7">
          {/* Copy + CTAs */}
          <div className="max-w-sm flex-shrink-0">
            <h3
              className="text-[#f7f8f8]"
              style={{ fontSize: '27px', fontWeight: 590, lineHeight: 1.1, letterSpacing: '-0.6px' }}
            >
              Cutting-edge <span style={{ color: '#b3bcff' }}>AI</span> models
            </h3>
            <p
              className="mt-2 text-white/65"
              style={{ fontSize: '13px', lineHeight: 1.5, letterSpacing: '-0.13px' }}
            >
              The models powering content generation across every campaign.
            </p>
          </div>

          {/* Provider cards — anchored right, rightmost dissolves into the gradient (design-aesthetics §32) */}
          <div className="hidden lg:flex flex-1 min-w-0 self-stretch items-center justify-end">
            <div
              className="flex h-full items-center gap-3 -mr-6 sm:-mr-7"
              style={{
                width: 'max-content',
                maskImage: 'linear-gradient(to right, #000 70%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to right, #000 70%, transparent 100%)',
              }}
            >
              {AI_MODELS.map((m) => (
                <div
                  key={m.name}
                  className="w-[184px] flex-shrink-0 rounded-xl border border-white/10 bg-white/[0.07] p-4 backdrop-blur-sm transition-colors duration-150 hover:bg-white/[0.11] hover:border-white/20"
                >
                  <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center overflow-hidden">
                    <img src={m.icon} alt={m.name} className="h-6 w-6 object-contain" />
                  </div>
                  <div
                    className="mt-3 text-[#f7f8f8]"
                    style={{ fontSize: '13.5px', fontWeight: 590, letterSpacing: '-0.13px' }}
                  >
                    {m.name}
                  </div>
                  <div className="mt-0.5 text-white/55" style={{ fontSize: '12px', lineHeight: 1.4 }}>
                    {m.tagline}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground dark:text-[#8a8f98]" />
          <input
            type="search"
            name="integration-search"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            data-form-type="other"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search integrations..."
            className="w-full h-9 pl-10 pr-4 bg-background dark:bg-white/[0.02] border border-border/60 dark:border-white/[0.08] hover:border-border/80 dark:hover:border-white/[0.14] rounded-md text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d] focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75 outline-none transition-colors duration-150"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Category tabs — indigo-soft active per §48 */}
      <div className="flex gap-1 overflow-x-auto pb-1 -mb-1 scrollbar-none">
        <button
          type="button"
          onClick={() => setActiveCategory('all')}
          className={cn(
            'flex items-center gap-1.5 px-3 h-8 rounded-full border transition-colors duration-150 whitespace-nowrap',
            activeCategory === 'all'
              ? 'bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30'
              : 'text-muted-foreground border-border/50 hover:bg-muted/50 hover:text-foreground dark:text-[#8a8f98] dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:text-[#f7f8f8]',
          )}
          style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
        >
          <GearSix className="w-3.5 h-3.5" />
          All
        </button>
        {CATEGORIES.map(cat => {
          const active = activeCategory === cat.id
          const connInCat = INTEGRATIONS.filter(i => i.category === cat.id && i.status === 'functional' && isConnected(i)).length
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveCategory(active ? 'all' : cat.id)}
              className={cn(
                'flex items-center gap-1.5 px-3 h-8 rounded-full border transition-colors duration-150 whitespace-nowrap',
                active
                  ? 'bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30'
                  : 'text-muted-foreground border-border/50 hover:bg-muted/50 hover:text-foreground dark:text-[#8a8f98] dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:text-[#f7f8f8]',
              )}
              style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
            >
              <span className={cn(active ? 'text-current' : 'text-muted-foreground dark:text-[#8a8f98]')}>{cat.icon}</span>
              {cat.label}
              {connInCat > 0 && (
                <span className={cn(
                  'w-4 h-4 rounded-full flex items-center justify-center tabular-nums',
                  active
                    ? 'bg-[#5e6ad2]/20 text-[#5e6ad2] dark:bg-[#5e6ad2]/25 dark:text-[#a5b4fc]'
                    : 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/[0.14] dark:text-emerald-300',
                )} style={{ fontSize: '10px', fontWeight: 590 }}>
                  {connInCat}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Integration groups */}
      <div className="space-y-2">
        {groupedIntegrations.map(({ category: cat, items }) => (
          <div key={cat.id}>
            {/* Category header (only show when viewing all) */}
            {activeCategory === 'all' && (
              <div className="flex items-center gap-2 py-2 px-2 mt-3 first:mt-0">
                <span className={cn('flex-shrink-0', cat.color)}>{cat.icon}</span>
                <span className="text-muted-foreground dark:text-[#8a8f98] uppercase tracking-[0.06em]" style={{ fontSize: '11px', fontWeight: 590 }}>
                  {cat.label}
                </span>
                <div className="flex-1 h-px bg-border/40 dark:bg-white/[0.06]" />
                <span className="text-muted-foreground dark:text-[#62666d] tabular-nums uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>
                  {items.filter(i => i.status === 'functional' && isConnected(i)).length}/{items.length}
                </span>
              </div>
            )}

            {/* Integration rows */}
            <div className="space-y-0">
              {items.map(int => (
                <IntegrationRow
                  key={int.id}
                  integration={int}
                  isConnected={int.status === 'functional' && isConnected(int)}
                  dotStatus={getDotStatus(int)}
                  isExpanded={expandedId === int.id}
                  onToggle={() => setExpandedId(expandedId === int.id ? null : int.id)}
                  config={config}
                  pendingKeys={pendingKeys}
                  setPendingKeys={setPendingKeys}
                  testIntegration={testIntegration}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {filteredIntegrations.length === 0 && (
        <div className="text-center py-16">
          <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
            <MagnifyingGlass className="w-5 h-5 text-muted-foreground dark:text-[#8a8f98]" />
          </div>
          <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>No integrations match your search.</p>
        </div>
      )}

      {/* Save button */}
      {canWrite && hasPendingChanges && (
        <div className="sticky bottom-4 flex justify-end pt-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            loading={isSaving}
            icon={<FloppyDisk className="w-4 h-4" weight="bold" />}
          >
            Save All Changes
          </Button>
        </div>
      )}
    </div>
  )
}

export default IntegrationsSection
